import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { UpdateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateEmailTemplateRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useUpdateEmailTemplateRPCClientImpl } from "../rpcs/RPC";
import { TemplateContent } from "@pulse/pulse-rpcs/lib/email-cycle/templateContent";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { RootStore } from "../../root/store/RootStore";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createEmailTemplateContentModel,
  EmailTemplateContentModel,
} from "../models/EmailTemplateContentModel";
import { BoundedName } from "@pulse/pulse-rpcs/lib/types/boundedName";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";

type UpdateEmailTemplateRPCError = Exclude<
  UpdateEmailTemplateRPC.RPCError,
  UpdateEmailTemplateRPC.RPCError.DuplicateTemplateName
>;

const getInitialState = (): SnapshotIn<typeof UpdateEmailTemplateStore> => ({
  templateId: EMPTY_CHARACTER,
  emailTemplateName: createBoundedNameModel(),
  emailTemplateContent: createEmailTemplateContentModel(),
  loadingUpdateEmailTemplateRPC: false,
  updateEmailTemplateRPCError: null,
});

export const UpdateEmailTemplateStore = types
  .model("UpdateEmailTemplateStore", {
    templateId: types.string,
    emailTemplateName: BoundedNameModel,
    emailTemplateContent: EmailTemplateContentModel,
    loadingUpdateEmailTemplateRPC: types.boolean,
    updateEmailTemplateRPCError: types.maybeNull(
      types.enumeration(
        "UpdateEmailTemplateRPCError",
        Object.values(UpdateEmailTemplateRPC.RPCError).filter(
          (error): error is UpdateEmailTemplateRPCError =>
            error !== UpdateEmailTemplateRPC.RPCError.DuplicateTemplateName,
        ),
      ),
    ),
  })
  .views((store) => ({
    get disableSubmit(): boolean {
      const emailTemplateDetailsStore =
        getRoot<typeof RootStore>(store).emailTemplateDetailsStore;

      const isEmptyData =
        !store.emailTemplateName.name || !store.emailTemplateContent.content;
      const hasAnyChanges =
        store.emailTemplateName.name ===
          emailTemplateDetailsStore.emailTemplateName.name &&
        store.emailTemplateContent.content ===
          emailTemplateDetailsStore.emailTemplateContent.content;
      const hasValidationError =
        store.emailTemplateName.validationError ??
        store.emailTemplateContent.validationError;

      return !!(isEmptyData || hasAnyChanges || hasValidationError);
    },
  }))
  .actions((store) => ({
    setTemplateId: (templateId: string): void => {
      store.templateId = templateId;
    },
    updateEmailTemplate: flow(function* () {
      try {
        store.loadingUpdateEmailTemplateRPC = true;
        store.updateEmailTemplateRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new UpdateEmailTemplateRPC.Request(
          new LeoUUID(store.templateId),
          new BoundedName(store.emailTemplateName.name),
          new TemplateContent(store.emailTemplateContent.content),
        );

        const result: LeoRPCResult<
          UpdateEmailTemplateRPC.Response,
          UpdateEmailTemplateRPC.Errors.Errors
        > =
          yield useUpdateEmailTemplateRPCClientImpl(apiClient).execute(request);
        if ("response" in result) {
          // Nothing to handle on successful response
        } else {
          switch (result.error.code) {
            case UpdateEmailTemplateRPC.RPCError.DuplicateTemplateName:
              store.emailTemplateName.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            case UpdateEmailTemplateRPC.RPCError.InvalidTemplateId:
            case UpdateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
              store.updateEmailTemplateRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingUpdateEmailTemplateRPC = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createUpdateEmailTemplateStore = (): Instance<
  typeof UpdateEmailTemplateStore
> => UpdateEmailTemplateStore.create(getInitialState());
